/**
 * SubMetricsManagePanel
 * ---------------------
 * The data-entry surface for a custom (target_metric kind) metric inside
 * the Employee Score → Manage → {metric} sub-tab. Replaces the legacy
 * <TargetsPage embedded /> block.
 *
 * Layout (per metric):
 *
 *   ┌─ Header  metric name · formula chip · [+ Manage metric →]
 *   │
 *   ├─ Per sub-metric:
 *   │   • source = 'manual'        → editable subjects × value table
 *   │   • source = 'task_question' → read-only "auto-derived" panel
 *   │   • source = 'sales_targets' → read-only figures from Sales & Targets,
 *   │                                 one row per person the API returned
 *   │
 *   ├─ Above the tabs, only when an input comes from Sales & Targets: one
 *   │   line saying where that month's figures stand, with at most one button
 *   │
 *   └─ Empty state (when no sub-metrics yet):
 *       "No sub-metrics configured. [+ Add sub-metric]"
 *
 * The metric's `attribution_scope` (chosen on the Configure tab for the
 * active role) decides whether the value tables show stores or employees
 * along the subject axis. The toggle is intentionally hidden — admins
 * already picked the scope on Configure.
 */
import PerEmployeeMetricScoreTable, {
  compareMetricScoreRows, initialMetricScoreSortDir, metricScoreSheet, type MetricScoreSortKey,
} from './PerEmployeeMetricScoreTable';
import { SearchInput } from './SearchInput';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Download, AlertTriangle } from 'lucide-react';
import {
  targetMetricsAPI, storesAPI, blobErrorMessage,
  type SalesFeedFigure, type SalesFeedState, type SubMetricSourceType,
} from '../services/api';
import {
  SalesTargetsProductsField, loadSalesProductOptions, salesFeedInputs, type SalesProductOption,
} from './SalesTargetsProductsField';
import { downloadXlsx } from '../utils/download';
import { buildCsv, downloadCsv } from '../utils/csv';
import type { TableSheet, TableSheetGetter } from '../utils/tableXlsx';
import { formatValidRange, isOutsideValidRange } from '../utils/validRange';
import { SortTh, useTableSort } from './dashboard/useTableSort';
import { resultSheet, valueGridSheet } from './subMetricSheets';
import { BlockLoader, InlineLoader } from './loading';

interface EligibleQ {
  id: string;
  question_text: string;
  question_type: string;
  task_id: string;
  task_title: string;
}

interface SubMetric {
  id: string;
  name: string;
  code: string;
  source_type: SubMetricSourceType;
  source_question_id: string | null;
  source_aggregation: 'sum' | 'avg' | 'latest' | null;
  /** 'sales_targets' only: which figure, and for which products (null = all). */
  source_sales_figure?: SalesFeedFigure | null;
  source_sales_products?: string[] | null;
  display_order: number;
  /** Valid range — values outside it are ignored when scoring. null = no limit. */
  valid_min?: number | null;
  valid_max?: number | null;
}

interface MetricMeta {
  id: number;
  metric_name: string;
  formula: string | null;
  /** How cohort functions group people; passed back untouched by the one-step set-up. */
  cohort_grouping?: unknown;
}

/**
 * Re-write the stored formula string so identifier tokens render as the
 * user-typed sub-metric names instead of the slug codes. The server
 * formula (`( test + test_2 ) / 100`) stays as-is on the backend; this
 * is purely a display transform for the chips on the Result and
 * Per-employee scores tabs. Codes whose name we don't know fall back to
 * the raw code (so a rename that hasn't propagated still shows
 * something).
 */
const formatFormulaWithNames = (formula: string, nameByCode: Map<string, string>): string => {
  if (!formula) return formula;
  return formula.replace(/[a-zA-Z_][a-zA-Z0-9_]*/g, (m) => nameByCode.get(m.toLowerCase()) ?? m);
};

interface StoreLite {
  id: string;
  store_code?: string;
  store_name?: string;
  city?: string;
  // Street address — pulled through to per-sub-metric tables so admins
  // can see which physical location a value row applies to without
  // bouncing to the master list.
  address?: string;
}
interface EmployeeLite {
  employee_code: string;
  employee_name: string;
  /** Set on somebody who has left, with their store on that month's list. */
  exit_date?: string | null;
  store_code?: string;
  store_name?: string;
  store_address?: string;
}

// Per-employee score row projected to a single metric. The parent
// computes these by filtering the role-aware /api/scorecard payload to
// just this metric's contributions. We accept them as a prop instead of
// refetching so this panel doesn't need its own scorecard API call.
export interface MetricScoreRow {
  employee_code: string;
  employee_name: string;
  store_name: string;
  store_code: string;
  store_address: string;
  value: number | null;
  points: number;
  contribution: number;
  weight_pct: number;
  // `quadrant` was deliberately dropped — it's a row-level summary across
  // all metrics combined, not a per-metric property. The overall Score
  // tab keeps the column; the per-metric Manage tab does not.
}

interface Props {
  /** target_metrics.id */
  metricId: number;
  /** Period in YYYY-MM-DD (first of month) — same as the rest of the page. */
  periodMonth: string;
  /** Project id (selected project). */
  projectId: number;
  /** Scope inherited from the metric's config row on Configure. */
  scope: 'store' | 'employee';
  /** Open the ManageMetricsModal pre-positioned to this metric. The
   *  parent owns the modal so we just emit the request. */
  onOpenManage: () => void;
  /** Per-employee score rows projected to this metric. Empty array =
   *  scorecard has no data yet for the current role. The Scores tab
   *  renders this read-only. */
  scoreRows: MetricScoreRow[];
  /** Loading / error signals for the scorecard fetch — surfaced inside
   *  the Scores tab so the panel can show a spinner. */
  scoreLoading: boolean;
  scoreError: string | null;
  /** Period switcher for the Scores tab. The data tables above this
   *  block read the same periodMonth, so changing it here re-evaluates
   *  everything in step. */
  onChangePeriod: (period: string) => void;
  /** Selected role id from the page's ROLE filter (null = no role
   *  selected). Threaded into the bulk template / upload calls so they
   *  scope their subject list to the role. */
  configRoleId?: string | null;
  /** Store codes that have at least one employee in the selected role.
   *  When non-null, the store-scope Value grid is filtered to just these.
   *  null = show all. */
  roleStoreCodes?: Set<string> | null;
  /** Suppress the per-section Month pickers. Set when the host page renders a
   *  single Month picker at the top of the page (the pickers all drive the same
   *  `periodMonth`, so the inline copies would be redundant duplicates). */
  hideMonthPicker?: boolean;
  /** Bumped by the host after the metric's calculation is edited, so the
   *  sub-metric list reloads without remounting the panel. */
  reloadToken?: number;
  /** The open inner tab fills this with a getter for the table it is showing; null whenever no table with rows is on screen. */
  exportRef?: React.MutableRefObject<TableSheetGetter | null>;
  /** Told true when the open inner tab has at least one row to export, false otherwise (loading, error, empty, no sub-metrics, unmounted). */
  onExportableChange?: (exportable: boolean) => void;
  /** Told after a month's Sales & Targets figures were stored from here, so the
   *  host reads the scores again: they are worked out from those figures. */
  onSalesFeedChanged?: () => void;
}

/** Which calculation version scores the selected month (from listSubMetrics). */
interface CalcVersionInfo {
  effective_from: string;
  next_effective_from: string | null;
  versions: string[];
}

const CALC_BASE_VERSION = '1900-01-01';
const calcVersionLabel = (v: string): string =>
  v === CALC_BASE_VERSION
    ? 'Initial'
    : new Date(`${v}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
/** "the Jul 2026 calculation (Jul 2026 onwards)", "the Initial calculation (all months through Jun 2026)". */
const describeCalcVersion = (cv: CalcVersionInfo): string => {
  let through: string | null = null;
  if (cv.next_effective_from) {
    const d = new Date(`${cv.next_effective_from}T00:00:00Z`);
    d.setUTCMonth(d.getUTCMonth() - 1);
    through = calcVersionLabel(d.toISOString().slice(0, 10));
  }
  const range = cv.effective_from === CALC_BASE_VERSION
    ? (through ? `all months through ${through}` : 'all months')
    : (through ? `${calcVersionLabel(cv.effective_from)} – ${through}` : `${calcVersionLabel(cv.effective_from)} onwards`);
  return `the ${calcVersionLabel(cv.effective_from)} calculation (${range})`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Sales & Targets — where a month's figures stand
// ─────────────────────────────────────────────────────────────────────────────
//
// An input read from Sales & Targets is not uploaded here. Its figures are
// stored once the month is over and its sales are in; where a project uploads
// its sales, "in" is an admin pressing Use these sales. Until then the table
// shows the figures so far, and they are not used for scores.

/** "October" — the month on screen, as the status line names it. */
const monthNameOf = (periodMonth: string): string => {
  const d = new Date(`${periodMonth.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? periodMonth
    : d.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' });
};

// Put together from its parts rather than left to a locale: en-GB writes
// September as "Sept", and en-US puts the month first.
const DAY_AND_MONTH = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });
/** "5 Nov" — the day a timestamp from the API falls on in India; '' when it has none. */
const dayAndMonth = (iso: string | null): string => {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  const parts = DAY_AND_MONTH.formatToParts(d);
  const part = (type: 'day' | 'month'): string => parts.find((x) => x.type === type)?.value ?? '';
  return `${part('day')} ${part('month')}`;
};

type SalesFeedButton = 'Use these sales' | 'Refresh' | 'Try again';

/**
 * The one line (and at most one button) for a month's Sales & Targets figures.
 * null = nothing to say: no scorecard carries the parameter that month, so no
 * figures are due and a promise of them would be false.
 */
const salesFeedLine = (
  state: SalesFeedState,
  periodMonth: string,
): { text: string; button: SalesFeedButton | null } | null => {
  const month = monthNameOf(periodMonth);
  if (state.status === 'running' || !state.month_over) {
    return { text: `${month} is still running. Showing sales so far.`, button: null };
  }
  if (state.status === 'failed') return { text: 'Could not read Sales & Targets.', button: 'Try again' };
  if (state.status === 'stored') {
    const on = dayAndMonth(state.stored_at);
    const people = `${state.people.toLocaleString('en-IN')} ${state.people === 1 ? 'person' : 'people'}`;
    // People with sales but no target are not scored on the parameter. Counted
    // on the line, so the missing targets are noticed. Only for a stored month:
    // the count is of the figures that were stored.
    const soldNoTarget = state.sold_no_target > 0
      ? ` · ${state.sold_no_target.toLocaleString('en-IN')} sold with no target`
      : '';
    const said = state.changed
      ? (on ? `Sales changed since ${on}.` : 'Sales changed.')
      : `${people}${on ? ` · updated ${on}` : ''}`;
    return { text: `${said}${soldNoTarget}`, button: 'Refresh' };
  }
  // Waiting for an admin: uploaded sales have no "this month is complete" mark.
  if (state.needs_release) {
    const on = dayAndMonth(state.last_sales_at);
    return {
      text: on ? `${month} sales last uploaded ${on}.` : `No ${month} sales yet.`,
      button: 'Use these sales',
    };
  }
  // Waiting for the nightly run (running stock, or no Sold input).
  if (state.status === 'waiting') return { text: `${month} figures arrive tonight.`, button: null };
  return null;
};

/** What the status line is showing, with the month it was read for. */
interface SalesFeedOnScreen {
  month: string;
  state: SalesFeedState;
}

export default function SubMetricsManagePanel({
  metricId, periodMonth, projectId, scope, onOpenManage,
  scoreRows, scoreLoading, scoreError, onChangePeriod,
  configRoleId = null, roleStoreCodes = null,
  hideMonthPicker = false, reloadToken = 0,
  exportRef, onExportableChange, onSalesFeedChanged,
}: Props) {
  // The sections below all report "has rows to export" through this one wrapper.
  // It remembers the last answer sent, so the host hears a value only when it
  // really changes — a setState handed in as the callback can't be made to loop,
  // and a section that mounts still loading doesn't repeat the "no" the section
  // before it left behind.
  const onExportableChangeRef = useRef(onExportableChange);
  onExportableChangeRef.current = onExportableChange;
  const lastExportable = useRef<boolean | null>(null);
  const reportExportable = useCallback((exportable: boolean) => {
    if (lastExportable.current === exportable) return;
    lastExportable.current = exportable;
    onExportableChangeRef.current?.(exportable);
  }, []);
  // The spinner, the error box and the set-up card hold no table, and no section
  // is mounted to say so — the host starts from "nothing to export".
  useEffect(() => {
    if (lastExportable.current === null) reportExportable(false);
  }, [reportExportable]);

  const [meta, setMeta] = useState<MetricMeta | null>(null);
  const [subs, setSubs] = useState<SubMetric[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A metric's sub-metrics can differ by month (ManageMetricsModal versions the
  // calculation), so they are loaded for `periodMonth`; `calcVersion` says which
  // version that is — surfaced only when the metric has more than one.
  const [calcVersion, setCalcVersion] = useState<CalcVersionInfo | null>(null);
  // Latest selected month, readable inside loadAll without making every month
  // switch re-run the full load (spinner + store / employee refetch).
  const periodMonthRef = useRef(periodMonth);
  periodMonthRef.current = periodMonth;

  // Subjects — stores or employees depending on scope.
  const [stores, setStores] = useState<StoreLite[]>([]);
  // The people of the month on screen, for the role: the list the template and the
  // upload use, so a month's table and its file show the same people.
  const [employees, setEmployees] = useState<EmployeeLite[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [employeesError, setEmployeesError] = useState<string | null>(null);
  // Eligible task-question sources for the simple value picker (only used
  // when the metric has zero sub-metrics yet).
  const [eligibleQuestions, setEligibleQuestions] = useState<EligibleQ[]>([]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Stores here; employees follow the month in the effect below. The active
      // sub-metric's source decides which list drives the table — manual+store →
      // stores, manual+employee → employees, task_question → always employees
      // (task answers are per-employee by definition).
      const month = periodMonthRef.current;
      const [legRes, storeRes, qRes] = await Promise.all([
        targetMetricsAPI.listSubMetrics(metricId, month),
        storesAPI.getAll(projectId),
        targetMetricsAPI.getEligibleSourceQuestions(projectId).catch(() => null),
      ]);
      // If the month moved on meanwhile, the sub-metric effect below loads that one.
      if (legRes.data.status === 'success' && month === periodMonthRef.current) {
        setMeta(legRes.data.data.metric);
        setSubs(legRes.data.data.sub_metrics || []);
        setCalcVersion(legRes.data.data.version ?? null);
      }
      if (qRes && qRes.data?.status === 'success') {
        setEligibleQuestions(qRes.data.data || []);
      }
      if (storeRes) {
        setStores(storeRes.data?.data || storeRes.data || []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load sub-metrics');
    } finally {
      setLoading(false);
    }
  }, [metricId, projectId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Emptied first, so no table or Download holds the previous month's people while
  // these load. A failed read says so: no fallback list that looks complete.
  useEffect(() => {
    let cancelled = false;
    setEmployees([]);
    setEmployeesLoading(true);
    setEmployeesError(null);
    targetMetricsAPI.getMonthPeople(metricId, periodMonth, configRoleId)
      .then((res) => { if (!cancelled) setEmployees(res.data?.data || []); })
      .catch((err: any) => {
        if (!cancelled) setEmployeesError(err?.response?.data?.message || 'Could not load the employees of this month.');
      })
      .finally(() => { if (!cancelled) setEmployeesLoading(false); });
    return () => { cancelled = true; };
  }, [metricId, periodMonth, configRoleId]);

  // Month switch, or the host saying the calculation changed → reload just the
  // sub-metrics for the selected month (the same metric can be bulk-uploaded in
  // June and task-derived from July). A superseded request is dropped.
  useEffect(() => {
    let cancelled = false;
    targetMetricsAPI.listSubMetrics(metricId, periodMonth)
      .then((res) => {
        if (cancelled || res.data.status !== 'success') return;
        setMeta(res.data.data.metric);
        setSubs(res.data.data.sub_metrics || []);
        setCalcVersion(res.data.data.version ?? null);
      })
      .catch(() => { /* keep what's shown — the sections surface their own load errors */ });
    return () => { cancelled = true; };
  }, [metricId, periodMonth, reloadToken]);

  // employee_code → store metadata, derived from the role-aware
  // scorecard rows the parent already passes in. The per-sub-metric
  // employee tables use this to surface Store Name / Code / Address
  // columns without a separate API call.
  const storeByEmployee = useMemo(() => {
    const m = new Map<string, { store_name: string; store_code: string; store_address: string }>();
    for (const r of scoreRows) {
      m.set(r.employee_code, {
        store_name: r.store_name ?? '',
        store_code: r.store_code ?? '',
        store_address: r.store_address ?? '',
      });
    }
    return m;
  }, [scoreRows]);

  // Two subject lists, kept ready for whichever the active sub-metric
  // needs. The Section component picks one based on its own source.
  // Both shapes share the same surface (store_name / store_code /
  // store_address) so the table layout in SubMetricSection can match
  // the Per-employee scores table for visual consistency.
  // Role scoping — when the page has a role selected, stores narrow to those
  // holding at least one role employee (null = no role selected → show all). The
  // employee list comes narrowed from the server.
  const storeSubjects = useMemo<Subject[]>(() => stores
    .filter((s) => !roleStoreCodes || roleStoreCodes.has(s.store_code || ''))
    .map((s) => ({
      kind: 'store',
      key: s.id,
      employee_code: '',
      employee_name: '',
      store_code: s.store_code || '—',
      store_name: s.store_name || '',
      store_address: s.address || '',
    })), [stores, roleStoreCodes]);
  const employeeSubjects = useMemo<Subject[]>(() => employees
    .map((e) => {
      const meta = storeByEmployee.get(e.employee_code);
      return {
        kind: 'employee',
        key: e.employee_code,
        employee_code: e.employee_code,
        employee_name: e.employee_name,
        exit_date: e.exit_date ?? null,
        store_code: meta?.store_code || e.store_code || '',
        store_name: meta?.store_name || e.store_name || '',
        store_address: meta?.store_address || e.store_address || '',
      };
    }), [employees, storeByEmployee]);

  // Active tab — sub-metric id, or the literal 'result' sentinel for
  // the trailing Result tab. Defaults to the first sub-metric. Survives
  // reloads while the same set of subs is in play; falls back when the
  // active id disappears (deleted from Manage Metrics, etc.).
  const [activeSubId, setActiveSubId] = useState<string | null>(null);
  useEffect(() => {
    if (subs.length === 0) { setActiveSubId(null); return; }
    // 'result' and 'scores' are synthetic ids that don't map to a
    // sub-metric row — keep them as the active tab when they're set.
    if (activeSubId === 'result' || activeSubId === 'scores') return;
    if (!activeSubId || !subs.some((s) => s.id === activeSubId)) {
      setActiveSubId(subs[0].id);
    }
  }, [subs, activeSubId]);

  // code → user-typed name lookup used to pretty-print the stored
  // formula on the Result and Per-employee scores tabs. The server
  // formula keeps its slug codes (so the parser stays happy); only
  // the display goes through this map.
  const subNameByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of subs) m.set(s.code.toLowerCase(), s.name);
    return m;
  }, [subs]);

  // ── Sales & Targets figures ────────────────────────────────────────────────
  // True when the calculation scoring the selected month has an input read from
  // Sales & Targets. Everything below is for such a parameter only.
  const hasSalesFeed = subs.some((s) => s.source_type === 'sales_targets');
  const [salesFeed, setSalesFeed] = useState<SalesFeedOnScreen | null>(null);
  const [salesFeedBusy, setSalesFeedBusy] = useState(false);
  // What the API answered when a press could not store the month.
  const [salesFeedProblem, setSalesFeedProblem] = useState<string | null>(null);
  // Bumped when a press stored the month: the status line, the open Sales &
  // Targets table and the Result tab all read it again.
  const [salesFeedNonce, setSalesFeedNonce] = useState(0);

  useEffect(() => {
    setSalesFeedProblem(null);
    if (!hasSalesFeed) { setSalesFeed(null); return; }
    let cancelled = false;
    targetMetricsAPI.getSalesFeedState(metricId, periodMonth)
      .then((res) => {
        if (cancelled) return;
        const state: SalesFeedState | null = res.data?.status === 'success' ? res.data.data ?? null : null;
        setSalesFeed(state ? { month: periodMonth, state } : null);
      })
      // No line rather than a wrong one — the tables below load on their own.
      .catch(() => { if (!cancelled) setSalesFeed(null); });
    return () => { cancelled = true; };
  }, [metricId, periodMonth, hasSalesFeed, reloadToken, salesFeedNonce]);

  // One press of the line's button. The API may ask a question before it stores
  // anything — nothing was sold; an older month would be recalculated — and the
  // same request is then sent again carrying the admin's yes.
  const storeSalesFeed = async (opts: { allow_empty?: boolean; confirm_older?: boolean } = {}): Promise<void> => {
    const month = periodMonth;
    try {
      const res = Object.keys(opts).length > 0
        ? await targetMetricsAPI.releaseSalesFeed(metricId, month, opts)
        : await targetMetricsAPI.releaseSalesFeed(metricId, month);
      // Stored. If the month on screen has moved on meanwhile, its own load covers it.
      if (month === periodMonthRef.current) {
        const state: SalesFeedState | null = res.data?.data ?? null;
        if (state) setSalesFeed({ month, state });
        setSalesFeedNonce((n) => n + 1);
      }
      onSalesFeedChanged?.();
    } catch (err: any) {
      const refused = err?.response?.data;
      if (refused?.code === 'NO_SALES' && !opts.allow_empty) {
        if (window.confirm(`No ${monthNameOf(month)} sales found. Score everyone on zero?`)) {
          await storeSalesFeed({ ...opts, allow_empty: true });
        }
        return;
      }
      if (refused?.code === 'CONFIRM_OLDER' && !opts.confirm_older) {
        // The API words this one: it knows how many people the month holds.
        if (window.confirm(refused.message || `Recalculate ${calcVersionLabel(month)}?`)) {
          await storeSalesFeed({ ...opts, confirm_older: true });
        }
        return;
      }
      setSalesFeedProblem(refused?.message || 'Could not read Sales & Targets.');
    }
  };
  const pressSalesFeed = async () => {
    if (salesFeedBusy) return;
    setSalesFeedBusy(true);
    setSalesFeedProblem(null);
    try {
      await storeSalesFeed();
    } finally {
      setSalesFeedBusy(false);
    }
  };

  // A parameter read from Sales & Targets is scored for managers too (on their
  // team's total), and a manager is not deployed at a store — so the deployed
  // list above would leave an Area Manager role with an empty Result tab. For
  // such a parameter the Result tab lists the people the host scored on it for
  // the chosen role: the same people as the Per-employee scores tab.
  const scoredSubjects = useMemo<Subject[]>(() => scoreRows.map((r) => ({
    kind: 'employee',
    key: r.employee_code,
    employee_code: r.employee_code,
    employee_name: r.employee_name,
    store_code: r.store_code ?? '',
    store_name: r.store_name ?? '',
    store_address: r.store_address ?? '',
  })), [scoreRows]);
  const resultSubjects = hasSalesFeed && scoredSubjects.length > 0 ? scoredSubjects : employeeSubjects;

  if (loading) {
    return (
      <BlockLoader className="py-8" />
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">{error}</div>
    );
  }

  // Empty state — no sub-metrics yet. Instead of forcing the admin to
  // think in terms of "legs + formula", offer the most common case as a
  // one-click setup: the metric has a single value per employee/store,
  // sourced either via manual upload or auto-derived from a task
  // question. The admin can always switch to the multi-leg model later
  // via Manage metric.
  if (subs.length === 0) {
    return (
      <SimpleValueSetup
        metricName={meta?.metric_name || 'This metric'}
        metricId={metricId}
        projectId={projectId}
        periodMonth={periodMonth}
        calcVersionStart={calcVersion?.effective_from ?? CALC_BASE_VERSION}
        cohortGrouping={meta?.cohort_grouping ?? null}
        eligibleQuestions={eligibleQuestions}
        onOpenManage={onOpenManage}
        onDone={loadAll}
      />
    );
  }

  const activeSub = subs.find((s) => s.id === activeSubId) ?? null;
  // Only for the month it was read for — a month switch must not leave the
  // previous month's line (and its button) standing over the new month's tables.
  const feedLine = hasSalesFeed && salesFeed && salesFeed.month === periodMonth
    ? salesFeedLine(salesFeed.state, periodMonth)
    : null;

  return (
    <div className="space-y-3">
      {/* Only once the calculation has changed over time: which version scores the
          selected month, so a task-derived July beside a bulk-uploaded June isn't a
          surprise. A metric with a single calculation renders exactly as before. */}
      {calcVersion && calcVersion.versions.length > 1 && (
        <p className="text-xs text-gray-500">
          This month is scored with {describeCalcVersion(calcVersion)}.
        </p>
      )}
      {/* Header card removed — the outer per-metric tab strip already
          labels the active metric (e.g. the "Demo" tab is selected),
          and the Manage Metrics modal is reachable from the Configure
          tab. A second "Demo · Manage metric" line here was pure
          duplication. The `onOpenManage` callback is still wired in
          via the SimpleValueSetup empty state above, where it's the
          natural CTA for new metrics. */}

      {/* Sales & Targets: where this month's figures stand. One line for the
          parameter, above the tabs — its inputs are stored together, so the
          line and its button are not repeated on every input's tab. */}
      {feedLine && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
          <span>{feedLine.text}</span>
          {feedLine.button && (
            <button
              type="button"
              onClick={pressSalesFeed}
              disabled={salesFeedBusy}
              className="inline-flex items-center gap-1 rounded-md bg-primary-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {salesFeedBusy && <Loader2 className="h-3 w-3 animate-spin" />}
              {feedLine.button}
            </button>
          )}
          {salesFeedProblem && (
            <span role="alert" className="text-xs text-rose-600">{salesFeedProblem}</span>
          )}
        </div>
      )}

      {/* Tab strip — one tab per sub-metric + a trailing Result tab. The
          Result tab evaluates the metric's formula over the period and
          shows the final per-employee value the scorecard engine
          consumes, so admins can sanity-check the maths before the
          Score tab. */}
      <div className="border-b border-gray-200">
        <div className="flex flex-wrap gap-1">
          {subs.map((sub) => {
            const active = sub.id === activeSubId;
            return (
              <button
                key={sub.id}
                onClick={() => setActiveSubId(sub.id)}
                className={`relative inline-flex items-center px-3 py-2 text-sm transition ${
                  active
                    ? 'text-primary-700 font-semibold'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                {/* Sub-metric tabs render the name only — the manual /
                    auto badge was redundant noise on the tab strip
                    (the source is re-stated where it actually matters,
                    inside the active section). */}
                <span>{sub.name}</span>
                {active && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-primary-600" />}
              </button>
            );
          })}
          {/* Result tab — only meaningful when at least one sub-metric
              and a formula exist. The component itself reports "not
              configured" gracefully when either is missing. */}
          {subs.length > 0 && (
            <button
              onClick={() => setActiveSubId('result')}
              className={`relative inline-flex items-center gap-2 px-3 py-2 text-sm transition ${
                activeSubId === 'result'
                  ? 'text-primary-700 font-semibold'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <span>Result</span>
              <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-primary-50 text-primary-700">
                formula
              </span>
              {activeSubId === 'result' && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-primary-600" />}
            </button>
          )}
          {/* Scores tab — per-employee weighted contribution for this
              metric, projected from the role-aware /api/scorecard payload.
              Was previously rendered stacked below; now it lives next to
              the sub-metric data so admins can flip between input and
              output without scrolling. */}
          <button
            onClick={() => setActiveSubId('scores')}
            className={`relative inline-flex items-center gap-2 px-3 py-2 text-sm transition ${
              activeSubId === 'scores'
                ? 'text-primary-700 font-semibold'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <span>Per-employee scores</span>
            <span className="rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-50 text-amber-700">
              slabs × weight
            </span>
            {activeSubId === 'scores' && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-primary-600" />}
          </button>
        </div>
      </div>

      {/* Active tab content */}
      {activeSubId === 'result' ? (
        <ResultSection
          metricId={metricId}
          metricName={meta?.metric_name ?? 'Metric'}
          formula={meta?.formula ?? null}
          subNameByCode={subNameByCode}
          periodMonth={periodMonth}
          onChangePeriod={onChangePeriod}
          employeeSubjects={resultSubjects}
          employeesLoading={resultSubjects === employeeSubjects && employeesLoading}
          employeesError={resultSubjects === employeeSubjects ? employeesError : null}
          hideMonthPicker={hideMonthPicker}
          roleId={configRoleId}
          refreshToken={salesFeedNonce + reloadToken}
          exportRef={exportRef}
          onExportableChange={reportExportable}
        />
      ) : activeSubId === 'scores' ? (
        <PerEmployeeScoresSection
          rows={scoreRows}
          loading={scoreLoading}
          error={scoreError}
          periodMonth={periodMonth}
          onChangePeriod={onChangePeriod}
          formula={meta?.formula ?? null}
          subNameByCode={subNameByCode}
          hideMonthPicker={hideMonthPicker}
          exportRef={exportRef}
          onExportableChange={reportExportable}
        />
      ) : activeSub ? (
        <SubMetricSection
          key={activeSub.id}
          sub={activeSub}
          storeSubjects={storeSubjects}
          employeeSubjects={employeeSubjects}
          employeesLoading={employeesLoading}
          employeesError={employeesError}
          storeByEmployee={storeByEmployee}
          scope={scope}
          periodMonth={periodMonth}
          onChangePeriod={onChangePeriod}
          roleId={configRoleId}
          hideMonthPicker={hideMonthPicker}
          refreshToken={salesFeedNonce + reloadToken}
          exportRef={exportRef}
          onExportableChange={reportExportable}
        />
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// useTableExport — hands the open section's table to the host's Download
// ─────────────────────────────────────────────────────────────────────────────
//
// The host page has one Download button, and the file it writes is the table
// on screen. Exactly one of the three sections below is mounted at a time, and
// each calls this hook. While a section has at least one row on screen,
// `exportRef` points at a getter for exactly that table and the section says
// "exportable"; while it is loading, empty or showing a message instead, it
// takes its getter back and says "not exportable".

type TableExportProps = Pick<Props, 'exportRef' | 'onExportableChange'>;

function useTableExport(
  { exportRef, onExportableChange }: TableExportProps,
  hasRows: boolean,
  buildSheet: () => TableSheet,
) {
  // Re-installed after every render, not just when `hasRows` flips: the getter
  // closes over that render's rows, so a Download always carries the latest
  // sort, search and typed values. A layout effect, so the getter is in step
  // with the table before the browser can deliver the next click.
  // On an inner-tab switch the leaving section cleans up while the arriving one
  // installs — a section only ever clears a getter that is still its own.
  useLayoutEffect(() => {
    if (!exportRef || !hasRows) return;
    const getter: TableSheetGetter = () => buildSheet();
    exportRef.current = getter;
    return () => {
      if (exportRef.current === getter) exportRef.current = null;
    };
  });

  useEffect(() => {
    onExportableChange?.(hasRows);
  }, [hasRows, onExportableChange]);
  // Once the section is gone, so is its table.
  useEffect(() => () => { onExportableChange?.(false); }, [onExportableChange]);
}

// ─────────────────────────────────────────────────────────────────────────────
// SubMetricSection — one card per leg
// ─────────────────────────────────────────────────────────────────────────────

// Common row shape for value-entry tables. `kind` tells the renderer
// which leading columns to emphasize (Employee Code/Name for the
// employee scope; Store Code/Name for the store scope). All shape
// fields are present on both kinds for layout uniformity — unused
// fields are just empty strings.
/** '12 Oct 2026', as the Exit Date column prints it. */
const exitDayLabel = (date?: string | null): string => (date
  ? new Date(`${date.slice(0, 10)}T00:00:00Z`)
    .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
  : '');

interface Subject {
  kind: 'store' | 'employee';
  key: string;
  employee_code: string;
  employee_name: string;
  /** Set on somebody who has left: the Exit Date column, shown only when someone has. */
  exit_date?: string | null;
  store_code: string;
  store_name: string;
  store_address: string;
}

interface SectionProps extends TableExportProps {
  sub: SubMetric;
  storeSubjects: Subject[];
  employeeSubjects: Subject[];
  /** The month's employee list is still loading, or could not be read. */
  employeesLoading?: boolean;
  employeesError?: string | null;
  /** employee_code → store, for the rows of a Sales & Targets input: those come
   *  from the API as code, name and value only. */
  storeByEmployee: Map<string, { store_name: string; store_code: string; store_address: string }>;
  scope: 'store' | 'employee';
  periodMonth: string;
  /** Shared Month-picker handler. Surfaced inside every sub-metric
   *  section so admins don't have to scroll to Per-employee scores to
   *  switch periods. */
  onChangePeriod: (period: string) => void;
  /** Selected role id — passed to the bulk template / upload calls so the
   *  Excel paths stay scoped to the role like the on-screen grid. */
  roleId?: string | null;
  /** Hide the inline Month picker (host page renders one at the top). */
  hideMonthPicker?: boolean;
  /** Changes when a Sales & Targets input's figures may have changed (a month
   *  was just stored); its table then reads them again. */
  refreshToken?: number;
}

// A Sales & Targets figure is a sum, and with multipliers it need not be whole.
// The cell prints a whole number as it is and anything else to two decimals; the
// file gets the same number.
const shownFigure = (value: number | null): number | null =>
  (value == null || Number.isInteger(value) ? value : Number(value.toFixed(2)));

function SubMetricSection({ sub, storeSubjects, employeeSubjects, employeesLoading = false, employeesError = null, storeByEmployee, scope, periodMonth, onChangePeriod, roleId = null, hideMonthPicker = false, refreshToken = 0, exportRef, onExportableChange }: SectionProps) {
  const isSalesFeed = sub.source_type === 'sales_targets';
  // task_question sub-metrics are inherently per-employee — task answers
  // are submitted by individual employees, not by stores. Use the employee
  // list regardless of the parent metric's nominal scope.
  // A Sales & Targets input is per person too: its figures are stored against
  // each employee (a manager's is the team's total), whatever the role's scope.
  const effectiveMode: 'store' | 'employee' =
    sub.source_type === 'task_question' || isSalesFeed ? 'employee' : scope;
  // Read-only as soon as the sub-metric definition says task_question (or
  // Sales & Targets). Robust against the API not yet returning the
  // auto_derived flag (e.g. before the controller is restarted) so admins can
  // never edit a value the engine is going to overwrite anyway.
  const isAutoDerived = sub.source_type === 'task_question' || isSalesFeed;

  // The people a Sales & Targets input lists are the rows the API returned for
  // the chosen role — managers included, who are in no deployed-staff list.
  const [feedPeople, setFeedPeople] = useState<Array<{ employee_code: string; employee_name: string }>>([]);
  // True while the figures shown are the live ones of a month not stored yet.
  const [preview, setPreview] = useState(false);
  // The figures could not be read. Said in place of the table: an empty one
  // would read as "nobody has a figure", which is a different thing.
  const [feedReadFailed, setFeedReadFailed] = useState(false);
  const feedSubjects = useMemo<Subject[]>(() => feedPeople.map((e) => {
    const store = storeByEmployee.get(e.employee_code);
    return {
      kind: 'employee',
      key: e.employee_code,
      employee_code: e.employee_code,
      employee_name: e.employee_name,
      store_code: store?.store_code ?? '',
      store_name: store?.store_name ?? '',
      store_address: store?.store_address ?? '',
    };
  }), [feedPeople, storeByEmployee]);
  const subjects = isSalesFeed ? feedSubjects : effectiveMode === 'store' ? storeSubjects : employeeSubjects;
  const listsEmployees = !isSalesFeed && effectiveMode !== 'store';

  // value map keyed by subject.key
  const [values, setValues] = useState<Map<string, number | null>>(new Map());
  const [originals, setOriginals] = useState<Map<string, number | null>>(new Map());
  const [loading, setLoading] = useState(false);
  // False until the first load below has finished. The section's very first
  // frame draws the grid with every value still blank (the load only starts
  // after it), and that frame is not a table to hand to Download.
  const [valuesLoaded, setValuesLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // subject key → month a value was carried from ("No value = last available
  // month": no answer this month, so scoring uses the last available month's).
  const [carriedFrom, setCarriedFrom] = useState<Map<string, string>>(new Map());
  // subject key → an answer outside the sub-metric's valid range, which scoring
  // ignores (the value shown is then the carried-forward one, if any).
  const [ignoredValues, setIgnoredValues] = useState<Map<string, number>>(new Map());

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await targetMetricsAPI.listSubMetricValues(sub.id, periodMonth, roleId);
      const next = new Map<string, number | null>();
      const carried = new Map<string, string>();
      const ignored = new Map<string, number>();
      // One row per person, in the order the API sent them.
      const people = new Map<string, string>();
      if (res.data.status === 'success') {
        for (const row of res.data.data || []) {
          const k = effectiveMode === 'store' ? row.store_id : row.employee_code;
          if (k) next.set(k, row.value == null ? null : Number(row.value));
          if (k && row.carried_from_month) carried.set(k, String(row.carried_from_month).slice(0, 10));
          if (k && row.ignored_value != null) ignored.set(k, Number(row.ignored_value));
          if (isSalesFeed && k && !people.has(k)) people.set(k, String(row.employee_name ?? ''));
        }
      }
      setValues(next);
      setOriginals(new Map(next));
      setCarriedFrom(carried);
      setIgnoredValues(ignored);
      setFeedPeople([...people].map(([employee_code, employee_name]) => ({ employee_code, employee_name })));
      setPreview(isSalesFeed && res.data.status === 'success' && res.data.preview === true);
      setFeedReadFailed(false);
    } catch (err) {
      // An uploaded grid keeps what it was showing, as it always has. A month
      // not stored yet is read live from Sales & Targets, and that read can fail.
      if (!isSalesFeed) throw err;
      setValues(new Map());
      setOriginals(new Map());
      setFeedPeople([]);
      setPreview(false);
      setFeedReadFailed(true);
    } finally {
      setValuesLoaded(true);
      setLoading(false);
    }
  }, [sub.id, periodMonth, effectiveMode, roleId, isSalesFeed]);

  // Uploaded values change only through this section's own Save / Upload, so
  // only a Sales & Targets table follows `refreshToken` — re-reading a manual
  // grid here would throw away what has been typed and not saved.
  const feedRefresh = isSalesFeed ? refreshToken : 0;
  useEffect(() => { reload(); }, [reload, feedRefresh]);

  const dirtyKeys = useMemo(() => {
    const out: string[] = [];
    for (const [k, v] of values) {
      const orig = originals.get(k);
      if ((orig ?? null) !== (v ?? null)) out.push(k);
    }
    return out;
  }, [values, originals]);

  // Column sort for the Value table. Value sorts by the SAVED figure
  // (`originals`), not the live input, so a row doesn't jump while it's being
  // typed in. '—' is storeSubjects' placeholder for a store with no code.
  const sort = useTableSort<Subject>(subjects, {
    employeeName: (s) => s.employee_name,
    employeeCode: (s) => s.employee_code,
    storeName: (s) => s.store_name,
    storeCode: (s) => (s.store_code === '—' ? null : s.store_code),
    storeAddress: (s) => s.store_address,
    exitDate: (s) => s.exit_date ?? null,
    value: (s) => originals.get(s.key) ?? null,
  });
  const anyLeft = effectiveMode !== 'store' && subjects.some((s) => !!s.exit_date);

  // The Value table as the host's Download writes it: the rows in the order
  // shown, and Value as the number each cell holds right now — for a manual
  // sub-metric that is the input, typed-but-unsaved edits included. The notes
  // are the ones only an auto-derived cell prints after its number.
  useTableExport(
    { exportRef, onExportableChange },
    valuesLoaded && !loading && subjects.length > 0,
    () => valueGridSheet(sub.name, effectiveMode, sort.sorted.map((s) => {
      const v = isSalesFeed ? shownFigure(values.get(s.key) ?? null) : values.get(s.key) ?? null;
      const carriedMonth = carriedFrom.get(s.key);
      return {
        ...s,
        exitDate: exitDayLabel(s.exit_date),
        value: v,
        carriedFrom: isAutoDerived && v != null && carriedMonth ? calcVersionLabel(carriedMonth) : null,
        ignoredValue: isAutoDerived ? ignoredValues.get(s.key) ?? null : null,
      };
    })),
  );

  const save = async () => {
    if (isAutoDerived || dirtyKeys.length === 0) return;
    setSaving(true);
    try {
      const rows = dirtyKeys.map((k) => {
        if (effectiveMode === 'store') return { store_id: k, value: values.get(k) ?? null };
        return { employee_code: k, value: values.get(k) ?? null };
      });
      await targetMetricsAPI.upsertSubMetricValues(sub.id, {
        period_month: periodMonth,
        scope_type: effectiveMode,
        rows,
      });
      await reload();
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 3000);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to save values');
    } finally {
      setSaving(false);
    }
  };

  // The inner "{sub.name} · {sub.code} · {source}" header strip was
  // removed — the active tab above already labels the sub-metric, so
  // the strip was duplicating info. The source / aggregation hint is
  // now folded into the toolbar's left-hand subtitle.
  const rangeLabel = formatValidRange(sub.valid_min, sub.valid_max);
  const sourceLine = (isSalesFeed
    ? 'From Sales & Targets. For a manager, the team\'s total.'
    : isAutoDerived
      ? `Auto-derived from task question — aggregated ${sub.source_aggregation ?? 'sum'} per employee for the period.`
      : 'Manual upload — fill the Value column or use Download / Upload to bulk-edit.')
    + (rangeLabel ? ` Valid range ${rangeLabel} — values outside it are ignored when scoring.` : '');

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      {/* Toolbar: source line on the left; Month picker + (manual only)
          bulk-upload controls + Save on the right. */}
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-3 py-2">
        <div className="min-w-0 text-xs text-gray-500">
          {/* The live figures of a month that is not stored yet: shown so a wrong
              set-up is seen straight away, and headed so nobody reads them as scored. */}
          {preview && !loading && (
            <div className="text-sm font-semibold text-amber-700">Not used for scores yet</div>
          )}
          {sourceLine}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {!hideMonthPicker && (
            <>
              <label className="text-xs text-gray-500">Month</label>
              <input
                type="month"
                value={periodMonth.slice(0, 7)}
                onChange={(e) => onChangePeriod(`${e.target.value}-01`)}
                className="rounded-md border border-gray-300 px-2 py-1 text-xs"
              />
            </>
          )}
          {!isAutoDerived && savedAt && <span className="text-[11px] text-emerald-700">Saved ✓</span>}
          {!isAutoDerived && (
            <BulkUploadControls
              subId={sub.id}
              periodMonth={periodMonth}
              scope={effectiveMode}
              roleId={roleId}
              onDone={reload}
            />
          )}
          {!isAutoDerived && (
            <button
              onClick={save}
              disabled={saving || dirtyKeys.length === 0}
              className="inline-flex items-center gap-1 rounded-md bg-primary-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-3 w-3 animate-spin" />}
              Save {dirtyKeys.length > 0 ? `(${dirtyKeys.length})` : ''}
            </button>
          )}
        </div>
      </div>

      {/* A Sales & Targets table has no rows of its own before its first load (the
          API names its people), so that first frame is a spinner too — not the
          "no figures" line flashing by. */}
      {loading || (isSalesFeed && !valuesLoaded) || (listsEmployees && employeesLoading) ? (
        <BlockLoader className="py-6" />
      ) : feedReadFailed ? (
        <p className="p-4 text-xs text-rose-600">Could not read Sales &amp; Targets.</p>
      ) : listsEmployees && employeesError ? (
        <p className="p-4 text-xs text-rose-600">{employeesError}</p>
      ) : subjects.length === 0 ? (
        <p className="p-4 text-xs text-gray-500">
          {isSalesFeed
            // Nobody the API returned: no one on the role has a figure this month.
            ? (roleId ? 'No figures for this role.' : 'No figures yet.')
            : `No ${effectiveMode === 'store' ? 'stores' : 'employees'} found for this project.`}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              {/* Column set matches the Per-employee scores table so the
                  page reads consistently across the metric tab. For
                  store-scope sub-metrics, Employee Code/Name collapse
                  to a single Store-led layout. */}
              {effectiveMode === 'store' ? (
                <tr>
                  <SortTh label="Store Code" sortKey="storeCode" sort={sort} className="whitespace-nowrap px-3 py-2 text-left" />
                  <SortTh label="Store Name" sortKey="storeName" sort={sort} className="whitespace-nowrap px-3 py-2 text-left" />
                  <SortTh label="Store Address" sortKey="storeAddress" sort={sort} className="whitespace-nowrap px-3 py-2 text-left" />
                  <SortTh label="Value" sortKey="value" sort={sort} align="right" className="whitespace-nowrap px-3 py-2 text-right" />
                </tr>
              ) : (
                <tr>
                  <SortTh label="Employee Name" sortKey="employeeName" sort={sort} className="whitespace-nowrap px-3 py-2 text-left" />
                  <SortTh label="Employee Code" sortKey="employeeCode" sort={sort} className="whitespace-nowrap px-3 py-2 text-left" />
                  <SortTh label="Store Name" sortKey="storeName" sort={sort} className="whitespace-nowrap px-3 py-2 text-left" />
                  <SortTh label="Store Code" sortKey="storeCode" sort={sort} className="whitespace-nowrap px-3 py-2 text-left" />
                  <SortTh label="Store Address" sortKey="storeAddress" sort={sort} className="whitespace-nowrap px-3 py-2 text-left" />
                  {anyLeft && <SortTh label="Exit Date" sortKey="exitDate" sort={sort} className="whitespace-nowrap px-3 py-2 text-left" />}
                  <SortTh label="Value" sortKey="value" sort={sort} align="right" className="whitespace-nowrap px-3 py-2 text-right" />
                </tr>
              )}
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sort.sorted.map((s) => {
                const v = values.get(s.key);
                const isDirty = !isAutoDerived && (originals.get(s.key) ?? null) !== (v ?? null);
                const carriedMonth = carriedFrom.get(s.key);
                const ignoredValue = ignoredValues.get(s.key);
                const outOfRange = !isAutoDerived && v != null && isOutsideValidRange(v, sub.valid_min, sub.valid_max);
                const valueCell = isAutoDerived ? (
                  // Auto-derived cells are read-only — the engine owns
                  // these values. Empty cell when no answer landed for
                  // the employee in the period — unless the config carries
                  // the last available month forward, which is labelled.
                  // An answer outside the valid range is shown as ignored.
                  <span className={`tabular-nums ${v == null ? 'text-gray-300' : 'text-gray-900'}`}>
                    {v == null ? '—' : isSalesFeed ? shownFigure(v) : v}
                    {v != null && carriedMonth && (
                      <span
                        className="ml-1.5 text-[11px] font-normal text-amber-700"
                        title="No valid answer this month — scoring uses the last available month's value (No value = last available month)."
                      >
                        from {calcVersionLabel(carriedMonth)}
                      </span>
                    )}
                    {ignoredValue != null && (
                      <span
                        className="ml-1.5 text-[11px] font-normal text-rose-600"
                        title={`The answer ${ignoredValue} is outside the valid range ${rangeLabel}, so scoring ignores it.`}
                      >
                        ignored {ignoredValue}
                      </span>
                    )}
                  </span>
                ) : (
                  <input
                    type="number"
                    inputMode="decimal"
                    value={v == null ? '' : String(v)}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const next = new Map(values);
                      if (raw === '') next.set(s.key, null);
                      else next.set(s.key, Number(raw));
                      setValues(next);
                    }}
                    title={outOfRange ? `Outside the valid range ${rangeLabel} — ignored when scoring.` : undefined}
                    className={`w-24 rounded-md border px-2 py-1 text-right text-sm focus:outline-none ${
                      outOfRange ? 'border-rose-400 text-rose-700 focus:border-rose-500' : 'border-gray-300 focus:border-primary-500'
                    }`}
                  />
                );
                if (effectiveMode === 'store') {
                  return (
                    <tr key={s.key} className={isDirty ? 'bg-amber-50/40' : ''}>
                      <td className="px-3 py-2 text-gray-700">{s.store_code || '—'}</td>
                      <td className="px-3 py-2 text-gray-700">{s.store_name || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{s.store_address || '—'}</td>
                      <td className="px-3 py-2 text-right">{valueCell}</td>
                    </tr>
                  );
                }
                return (
                  <tr key={s.key} className={isDirty ? 'bg-amber-50/40' : ''}>
                    <td className="px-3 py-2 font-medium text-gray-900">{s.employee_name || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{s.employee_code}</td>
                    <td className="px-3 py-2 text-gray-700">{s.store_name || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{s.store_code || '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{s.store_address || '—'}</td>
                    {anyLeft && <td className="whitespace-nowrap px-3 py-2 text-gray-700">{exitDayLabel(s.exit_date) || '—'}</td>}
                    <td className="px-3 py-2 text-right">{valueCell}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// SimpleValueSetup — empty-state card with the simple value-source picker
// ─────────────────────────────────────────────────────────────────────────────
//
// Shown when a custom metric has no sub-metrics yet. The admin picks how
// the metric will be measured (manual upload OR auto-derived from a task
// question) and clicks Set up — a single "value" sub-metric is created
// behind the scenes plus a formula of just `value`. Re-renders to the
// normal data-entry view with the employee/store table.
//
// The third source, Sales & Targets, is two inputs (Target and Sold) and the
// formula sold ÷ target, saved together in ONE definition save: saved one call
// at a time, a failure half-way would leave a parameter with an input and no
// formula, which holds its roles' month without ever scoring it.

interface SimpleValueSetupProps {
  metricName: string;
  metricId: number;
  /** The project — its Sales & Targets products decide whether that source is offered. */
  projectId: number;
  /** Month on screen — the new leg joins the calculation version that scores it. */
  periodMonth: string;
  /** Start of that calculation version (the first one, for a parameter never changed). */
  calcVersionStart: string;
  /** The parameter's cohort grouping as loaded; a definition save writes it back as it is. */
  cohortGrouping: unknown;
  eligibleQuestions: EligibleQ[];
  onOpenManage: () => void;
  onDone: () => Promise<void>;
}

function SimpleValueSetup({ metricName, metricId, projectId, periodMonth, calcVersionStart, cohortGrouping, eligibleQuestions, onOpenManage, onDone }: SimpleValueSetupProps) {
  const [sourceType, setSourceType] = useState<SubMetricSourceType>("manual");
  const [questionId, setQuestionId] = useState<string>(eligibleQuestions[0]?.id || "");
  const [agg, setAgg] = useState<"sum" | "avg" | "latest">("sum");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The project's Sales & Targets products; null until they are known. With none,
  // there are no targets or sales to read, so the source cannot be picked.
  const [salesProducts, setSalesProducts] = useState<SalesProductOption[] | null>(null);
  // null = all products; otherwise the chosen product keys.
  const [chosenProducts, setChosenProducts] = useState<string[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSalesProducts(null);
    loadSalesProductOptions(projectId)
      .then((options) => { if (!cancelled) setSalesProducts(options); })
      .catch(() => { if (!cancelled) setSalesProducts([]); });
    return () => { cancelled = true; };
  }, [projectId]);
  const noSalesProducts = !salesProducts || salesProducts.length === 0;
  // "Choose products" with nothing ticked is not "all products" (that is null),
  // and the API refuses an empty list.
  const nothingTicked = sourceType === "sales_targets" && chosenProducts !== null && chosenProducts.length === 0;

  const setup = async () => {
    setError(null);
    if (sourceType === "task_question" && !questionId) {
      setError("Pick a task question first");
      return;
    }
    setSaving(true);
    try {
      if (sourceType === "sales_targets") {
        // Target, Sold and the formula in one save. Nothing is stored by it:
        // the figures arrive once a month is over and its sales are in.
        const { formula, sub_metrics } = salesFeedInputs(chosenProducts);
        const definition = {
          effective_from: calcVersionStart,
          based_on: calcVersionStart,
          formula,
          cohort_grouping: cohortGrouping,
          sub_metrics,
        };
        try {
          await targetMetricsAPI.saveMetricDefinition(metricId, definition);
        } catch (e: any) {
          // Figures uploaded for the month still running would be replaced: the
          // API asks first, in its own words. Any other refusal is shown below.
          const refused = e?.response?.data;
          if (refused?.code !== "SALES_FEED_CONFIRM") throw e;
          if (!window.confirm(refused.message)) return;
          await targetMetricsAPI.saveMetricDefinition(metricId, { ...definition, confirm_replace: true });
        }
        await onDone();
        return;
      }
      await targetMetricsAPI.createSubMetric(metricId, {
        name: "Value",
        code: "value",
        source_type: sourceType,
        source_question_id: sourceType === "task_question" ? questionId : null,
        source_aggregation: sourceType === "task_question" ? agg : null,
        display_order: 0,
        effective_month: periodMonth,
      });
      // Auto-pin the formula to the single leg so the score engine knows
      // what to feed the slabs. Admins who later add more legs can rewrite
      // the formula via Manage metric.
      await targetMetricsAPI.updateFormula(metricId, "value", periodMonth);
      await onDone();
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to set up value source");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-gray-800">
          How are values measured for {metricName}?
        </h3>
        <p className="text-xs text-gray-500">
          Pick a source to start capturing data. You can switch to multiple
          sub-metrics + a formula later via <strong>Manage metric</strong>.
        </p>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="space-y-2">
        <label className="flex items-start gap-2 rounded-md border border-gray-200 p-2 cursor-pointer hover:bg-gray-50">
          <input
            type="radio"
            className="mt-0.5"
            checked={sourceType === "manual"}
            onChange={() => setSourceType("manual")}
          />
          <div>
            <div className="text-sm font-medium text-gray-800">Manual upload</div>
            <div className="text-xs text-gray-500">
              Admins enter the value per employee or store on this tab each
              period.
            </div>
          </div>
        </label>

        <label className={`flex items-start gap-2 rounded-md border border-gray-200 p-2 cursor-pointer ${eligibleQuestions.length === 0 ? "opacity-50" : "hover:bg-gray-50"}`}>
          <input
            type="radio"
            className="mt-0.5"
            checked={sourceType === "task_question"}
            disabled={eligibleQuestions.length === 0}
            onChange={() => setSourceType("task_question")}
          />
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-800">From task question</div>
            <div className="text-xs text-gray-500 mb-1">
              Auto-aggregate each employee’s numeric answers to a flagged task
              question over the period (sum / average / latest).
            </div>
            {sourceType === "task_question" && (
              <div className="grid grid-cols-[1fr_auto] gap-2 mt-1">
                <select
                  value={questionId}
                  onChange={(e) => setQuestionId(e.target.value)}
                  className="rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-primary-500 focus:outline-none"
                >
                  <option value="">— pick a question —</option>
                  {eligibleQuestions.map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.task_title} · {q.question_text.slice(0, 60)}{q.question_text.length > 60 ? "…" : ""}
                    </option>
                  ))}
                </select>
                <select
                  value={agg}
                  onChange={(e) => setAgg(e.target.value as any)}
                  className="rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-primary-500 focus:outline-none"
                >
                  <option value="sum">Sum</option>
                  <option value="avg">Average</option>
                  <option value="latest">Latest</option>
                </select>
              </div>
            )}
            {sourceType !== "task_question" && eligibleQuestions.length === 0 && (
              <div className="text-[11px] text-gray-400">
                No eligible numeric task questions in this project yet.
              </div>
            )}
          </div>
        </label>

        {/* Not a <label> like the two above: the products field inside holds
            controls of its own, and a click on one must not land on this radio. */}
        <div className={`flex items-start gap-2 rounded-md border border-gray-200 p-2 ${noSalesProducts ? "opacity-50" : "hover:bg-gray-50"}`}>
          <input
            id={`value-source-sales-${metricId}`}
            type="radio"
            className="mt-0.5"
            checked={sourceType === "sales_targets"}
            disabled={noSalesProducts}
            onChange={() => setSourceType("sales_targets")}
          />
          <div className="flex-1">
            <label
              htmlFor={`value-source-sales-${metricId}`}
              className={`block ${noSalesProducts ? "" : "cursor-pointer"}`}
            >
              <div className="text-sm font-medium text-gray-800">Sales &amp; Targets</div>
              <div className="text-xs text-gray-500 mb-1">Sold ÷ target, from Sales &amp; Targets.</div>
            </label>
            {sourceType === "sales_targets" && salesProducts && (
              <SalesTargetsProductsField
                options={salesProducts}
                value={chosenProducts}
                onChange={setChosenProducts}
                disabled={saving}
              />
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-1">
        <button
          onClick={onOpenManage}
          className="text-xs text-gray-500 hover:text-gray-700 underline"
        >
          Need multiple sub-metrics + a formula? Open Manage metric →
        </button>
        <button
          onClick={setup}
          disabled={saving || nothingTicked}
          className="inline-flex items-center gap-1 rounded-md bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Set up
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ResultSection — formula's per-employee output
// ─────────────────────────────────────────────────────────────────────────────
//
// Calls /target-metrics/:id/formula-values which runs the same evaluator
// the score engine uses. Read-only by construction — the value is a
// computed combination of the sub-metrics, not a stored cell. Cells with
// no input data render as `—`.

interface ResultSectionProps extends TableExportProps {
  metricId: number;
  metricName: string;
  formula: string | null;
  /** code → user-typed name lookup. The chip rendered in the toolbar
   *  swaps slug identifiers for these labels at display time. */
  subNameByCode: Map<string, string>;
  periodMonth: string;
  /** Same Month-picker handler used by the other sub-metric sections. */
  onChangePeriod: (period: string) => void;
  /** Enriched employee list — carries store_name / store_code /
   *  store_address so the table columns match Per-employee scores. For a
   *  parameter read from Sales & Targets it is the people scored on it for the
   *  chosen role (managers included), not the deployed staff. */
  employeeSubjects: Subject[];
  employeesLoading?: boolean;
  employeesError?: string | null;
  /** Hide the inline Month picker (host page renders one at the top). */
  hideMonthPicker?: boolean;
  /** Selected role — the result then reflects its "No value = last available
   *  month" carry-forward, exactly as the score engine computes it. */
  roleId?: string | null;
  /** Changes when the inputs may have changed underneath (a month of Sales &
   *  Targets figures was just stored); the result is then worked out again. */
  refreshToken?: number;
}

function ResultSection({ metricId, metricName, formula, subNameByCode, periodMonth, onChangePeriod, employeeSubjects, employeesLoading = false, employeesError = null, hideMonthPicker = false, roleId = null, refreshToken = 0, exportRef, onExportableChange }: ResultSectionProps) {
  const [values, setValues] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(false);
  // False until the first load below has finished — the first frame draws the
  // table with every Result still blank, which is not a table to hand to Download.
  const [valuesLoaded, setValuesLoaded] = useState(false);
  const [serverFormula, setServerFormula] = useState<string | null>(null);
  const [emptyMessage, setEmptyMessage] = useState<string | null>(null);
  // employee → month the result's input was carried from ("No value = last
  // available month": no answer this month, so the last available value is used).
  const [carriedFrom, setCarriedFrom] = useState<Map<string, string>>(new Map());

  // Column sort for the Result table; Result sorts by the computed value shown.
  const sort = useTableSort<Subject>(employeeSubjects, {
    employeeName: (s) => s.employee_name,
    employeeCode: (s) => s.employee_code,
    storeName: (s) => s.store_name,
    storeCode: (s) => s.store_code,
    storeAddress: (s) => s.store_address,
    exitDate: (s) => s.exit_date ?? null,
    result: (s) => values.get(s.key) ?? null,
  });
  const anyLeft = employeeSubjects.some((s) => !!s.exit_date);

  const load = useCallback(async () => {
    setLoading(true);
    setEmptyMessage(null);
    try {
      const res = await targetMetricsAPI.getFormulaValues(metricId, periodMonth, roleId);
      const next = new Map<string, number>();
      const carried = new Map<string, string>();
      if (res.data.status === 'success') {
        for (const row of res.data.data || []) {
          if (row.employee_code != null && row.value != null) {
            next.set(String(row.employee_code), Number(row.value));
            if (row.carried_from_month) carried.set(String(row.employee_code), String(row.carried_from_month).slice(0, 10));
          }
        }
        setServerFormula(res.data.formula ?? null);
        if ((res.data.data || []).length === 0) {
          setEmptyMessage(res.data.message ?? null);
        }
      }
      setValues(next);
      setCarriedFrom(carried);
    } finally {
      setValuesLoaded(true);
      setLoading(false);
    }
  }, [metricId, periodMonth, roleId]);

  useEffect(() => { load(); }, [load, refreshToken]);

  // The Result table as the host's Download writes it: the rows in the order shown.
  useTableExport(
    { exportRef, onExportableChange },
    valuesLoaded && !loading && employeeSubjects.length > 0,
    () => resultSheet(sort.sorted.map((s) => {
      const v = values.get(s.key) ?? null;
      const carriedMonth = carriedFrom.get(s.key);
      return {
        ...s,
        exitDate: exitDayLabel(s.exit_date),
        value: v,
        carriedFrom: v != null && carriedMonth ? calcVersionLabel(carriedMonth) : null,
      };
    })),
  );

  // Pretty-print the server formula by swapping slug codes with the
  // user-typed sub-metric names. Falls back to the raw formula when
  // the name lookup is empty (e.g. before the panel finishes loading).
  const rawFormula = serverFormula ?? formula;
  const displayFormula = rawFormula ? formatFormulaWithNames(rawFormula, subNameByCode) : null;

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      {/* Toolbar — formula chip + Month picker. Mirrors the pattern in
          SubMetricSection and PerEmployeeScoresSection for consistency. */}
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-3 py-2">
        <span className="text-xs text-gray-500">{metricName} — formula result, feeds the Configure-tab slabs.</span>
        {displayFormula ? (
          <code className="rounded border border-gray-200 bg-gray-50 px-2 py-0.5 font-mono text-[11px] text-gray-700">
            {displayFormula}
          </code>
        ) : (
          <span className="text-[11px] text-amber-700">
            No formula set — open Manage metric to define one.
          </span>
        )}
        {!hideMonthPicker && (
          <div className="ml-auto flex items-center gap-2">
            <label className="text-xs text-gray-500">Month</label>
            <input
              type="month"
              value={periodMonth.slice(0, 7)}
              onChange={(e) => onChangePeriod(`${e.target.value}-01`)}
              className="rounded-md border border-gray-300 px-2 py-1 text-xs"
            />
          </div>
        )}
      </div>

      {loading || employeesLoading ? (
        <BlockLoader className="py-6" />
      ) : employeesError ? (
        <p className="p-4 text-xs text-rose-600">{employeesError}</p>
      ) : employeeSubjects.length === 0 ? (
        <p className="p-4 text-xs text-gray-500">No employees found for this project.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <SortTh label="Employee Name" sortKey="employeeName" sort={sort} className="whitespace-nowrap px-3 py-2 text-left" />
                <SortTh label="Employee Code" sortKey="employeeCode" sort={sort} className="whitespace-nowrap px-3 py-2 text-left" />
                <SortTh label="Store Name" sortKey="storeName" sort={sort} className="whitespace-nowrap px-3 py-2 text-left" />
                <SortTh label="Store Code" sortKey="storeCode" sort={sort} className="whitespace-nowrap px-3 py-2 text-left" />
                <SortTh label="Store Address" sortKey="storeAddress" sort={sort} className="whitespace-nowrap px-3 py-2 text-left" />
                {anyLeft && <SortTh label="Exit Date" sortKey="exitDate" sort={sort} className="whitespace-nowrap px-3 py-2 text-left" />}
                <SortTh label="Result" sortKey="result" sort={sort} align="right" className="whitespace-nowrap px-3 py-2 text-right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sort.sorted.map((s) => {
                const v = values.get(s.key);
                return (
                  <tr key={s.key}>
                    <td className="px-3 py-2 font-medium text-gray-900">{s.employee_name || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{s.employee_code}</td>
                    <td className="px-3 py-2 text-gray-700">{s.store_name || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{s.store_code || '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{s.store_address || '—'}</td>
                    {anyLeft && <td className="whitespace-nowrap px-3 py-2 text-gray-700">{exitDayLabel(s.exit_date) || '—'}</td>}
                    <td className="px-3 py-2 text-right tabular-nums">
                      <span className={v == null ? 'text-gray-300' : 'font-medium text-gray-900'}>
                        {v == null ? '—' : Number.isInteger(v) ? v : v.toFixed(2)}
                      </span>
                      {v != null && carriedFrom.has(s.key) && (
                        <span
                          className="ml-1.5 text-[11px] font-normal text-amber-700"
                          title="No valid answer this month — computed from the last available month's value (No value = last available month)."
                        >
                          from {calcVersionLabel(carriedFrom.get(s.key)!)}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {emptyMessage && values.size === 0 && (
            <p className="p-2 text-[11px] text-gray-500">{emptyMessage}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PerEmployeeScoresSection — projected rows from /api/scorecard
// ─────────────────────────────────────────────────────────────────────────────
//
// The parent owns the role-aware scorecard fetch and projects the rows to
// this metric. Here we render them as a read-only table: raw value →
// points → weighted contribution → quadrant. Default-sorted by contribution
// desc (strongest performers on top); the three numeric headers are
// click-sortable, mirroring the SortableTh idiom on AppleScorecardPage.

// Which numeric column the per-employee table is sorted by. `value` can be
// null (employee outside the metric / no submission); those rows sink to the
// bottom regardless of direction.
type ScoreSortKey = MetricScoreSortKey;

interface PerEmployeeScoresSectionProps extends TableExportProps {
  rows: MetricScoreRow[];
  loading: boolean;
  error: string | null;
  periodMonth: string;
  onChangePeriod: (period: string) => void;
  /** Formula string for the parent Custom metric, e.g. "achievement /
   *  target * 100". Used to name the Raw value source so admins can
   *  trace each row back to the formula they configured. Null when the
   *  metric has no formula yet. */
  formula: string | null;
  /** code → user-typed name lookup. The subtitle's formula chip swaps
   *  slug identifiers for these labels at display time. */
  subNameByCode: Map<string, string>;
  /** Hide the inline Month picker (host page renders one at the top). */
  hideMonthPicker?: boolean;
}

function PerEmployeeScoresSection({ rows, loading, error, periodMonth, onChangePeriod, formula, subNameByCode, hideMonthPicker = false, exportRef, onExportableChange }: PerEmployeeScoresSectionProps) {
  const [sortKey, setSortKey] = useState<ScoreSortKey>('contribution');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const handleSort = (col: ScoreSortKey) => {
    if (col === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(col); setSortDir(initialMetricScoreSortDir(col)); }
  };
  // Free-text search — matches employee name/code and store name/code
  // (case-insensitive substring). Empty query → show everyone.
  const [search, setSearch] = useState('');
  // Blanks always sink to the bottom, regardless of direction.
  const sorted = useMemo(
    () => [...rows].sort((a, b) => compareMetricScoreRows(a, b, sortKey, sortDir)),
    [rows, sortKey, sortDir],
  );
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((r) =>
      `${r.employee_name ?? ''} ${r.employee_code ?? ''} ${r.store_name ?? ''} ${r.store_code ?? ''}`
        .toLowerCase()
        .includes(q),
    );
  }, [sorted, search]);
  const weightPct = sorted[0]?.weight_pct ?? 0;

  // The scores table as the host's Download writes it: the sorted, searched rows
  // on screen. A search that matches nobody leaves only a message, so no table.
  useTableExport(
    { exportRef, onExportableChange },
    !loading && !error && visible.length > 0,
    () => metricScoreSheet('Per-employee scores', visible, weightPct),
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-3 py-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-gray-800">Per-employee scores</div>
          <div className="text-xs text-gray-500">
            How this metric scores against the slabs you've configured for the selected role.
          </div>
          {/* Raw value source line — the column shows the formula's
              evaluated result per employee (same number as the Result
              tab). Surfacing the formula here removes the "where does
              this number come from?" ambiguity for Custom metrics. */}
          <div className="mt-1 text-xs text-gray-500">
            Raw value ={' '}
            {formula ? (
              <>
                <code className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[11px] text-gray-700">
                  {formatFormulaWithNames(formula, subNameByCode)}
                </code>
                <span className="ml-1">— formula evaluated per employee (same number as the <strong>Result</strong> tab).</span>
              </>
            ) : (
              <em className="text-amber-700">not configured — add a formula via the Manage Metrics modal before this metric can score.</em>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput value={search} onChange={setSearch} />
          {!hideMonthPicker && (
            <>
              <label className="text-xs text-gray-500">Month</label>
              <input
                type="month"
                value={periodMonth.slice(0, 7)}
                onChange={(e) => onChangePeriod(`${e.target.value}-01`)}
                className="rounded-md border border-gray-300 px-2 py-1 text-xs"
              />
            </>
          )}
        </div>
      </div>
      {loading ? (
        <div className="p-6 text-sm text-gray-500"><InlineLoader label="Loading…" /></div>
      ) : error ? (
        <div className="p-6 text-sm text-rose-600">{error}</div>
      ) : sorted.length === 0 ? (
        <div className="p-6 text-sm text-gray-500">
          No employee in any role's scorecard includes this metric for the selected month. Open
          Configure, tick this metric for a role, save, then return here.
        </div>
      ) : (
        <PerEmployeeMetricScoreTable
          rows={visible.map((r) => ({
            employee_code: r.employee_code,
            employee_name: r.employee_name,
            store_name: r.store_name,
            store_code: r.store_code,
            store_address: r.store_address,
            value: r.value,
            points: r.points,
            contribution: r.contribution,
          }))}
          weightPct={weightPct}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={handleSort}
          searchTerm={search}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BulkUploadControls — template download + xlsx import for manual sub-metrics
// ─────────────────────────────────────────────────────────────────────────────
//
// Two compact buttons co-located with Save so the workflow is one place:
//   1. Download — pulls a pre-filled xlsx (subjects ready, Value column blank)
//   2. Upload   — opens a hidden <input type="file">; on selection we read the
//                 file as base64 and POST to /bulk-upload. Server returns
//                 inserted/updated/skipped/rejected counts which we surface
//                 inline. Triggers the parent's reload so the table reflects
//                 the newly uploaded values without a manual refresh.

interface BulkUploadControlsProps {
  subId: string;
  periodMonth: string;
  scope: 'store' | 'employee';
  /** Selected role id — scopes the template/upload to the role's subjects. */
  roleId?: string | null;
  onDone: () => Promise<void>;
}

function BulkUploadControls({ subId, periodMonth, scope, roleId = null, onDone }: BulkUploadControlsProps) {
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  // Rows the last upload could NOT save (bad value / unresolvable subject). Held so the
  // user can download an actionable error list, fix those rows, and re-upload just them.
  // The valid rows in the SAME file were still saved (partial upload) — this is only the tail.
  const [rejected, setRejected] = useState<Array<{ row: number; name: string; code: string; value: string; reason: string }>>([]);

  // Build + download the rejected rows as a CSV so the uploader can see exactly which
  // employees/stores failed, the value they'd entered, and why — then fix and re-upload.
  const downloadErrors = () => {
    const codeLabel = scope === 'store' ? 'Store Code' : 'Employee Code';
    const nameLabel = scope === 'store' ? 'Store Name' : 'Employee Name';
    const headers = ['Row in file', nameLabel, codeLabel, 'Value entered', 'Error reason'];
    // The reasons contain both `"` and commas (e.g. (Hidden: "x", Code: "y")). buildCsv
    // quotes and escapes every cell itself, so pass them through untouched — escaping
    // here as well would double every quote in Excel.
    const rows = rejected.map((e) => [String(e.row), e.name || '', e.code || '', e.value || '', e.reason || '']);
    downloadCsv(`sub_metric_${subId}_${periodMonth}_upload_errors.csv`, buildCsv(headers, rows));
  };

  const download = async () => {
    setDownloading(true);
    setFeedback(null);
    try {
      const res = await targetMetricsAPI.downloadSubMetricTemplate(subId, periodMonth, scope, roleId);
      // Server sets a sensible filename via Content-Disposition; fall back
      // to a generic one for browsers that don't honour the header on blob
      // downloads.
      downloadXlsx(`sub_metric_${subId}_${periodMonth}.xlsx`, res.data);
    } catch (err: any) {
      // The request asks for a file, so a refusal's message arrives as a Blob.
      setFeedback(await blobErrorMessage(err, 'Failed to download template'));
    } finally {
      setDownloading(false);
    }
  };

  const upload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setUploading(true);
    setFeedback(null);
    setRejected([]);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const result = reader.result as string;
        // result is a data URL like "data:...;base64,XXXX" — strip the prefix.
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        const res = await targetMetricsAPI.bulkUploadSubMetric(subId, {
          period_month: periodMonth,
          scope,
          data: base64,
          role_id: roleId,
        });
        const r = res.data || {};
        const parts: string[] = [];
        if (r.inserted) parts.push(`${r.inserted} added`);
        if (r.updated) parts.push(`${r.updated} updated`);
        if (r.skipped) parts.push(`${r.skipped} skipped (blank)`);
        const rej = Array.isArray(r.rejected) ? r.rejected : [];
        if (rej.length > 0) parts.push(`${rej.length} not uploaded — download the error list to fix`);
        setRejected(rej);
        setFeedback(parts.length > 0 ? parts.join(' · ') : 'No changes');
        await onDone();
      } catch (err: any) {
        setFeedback(err?.response?.data?.message || 'Failed to upload file');
      } finally {
        setUploading(false);
      }
    };
    reader.onerror = () => {
      setFeedback('Failed to read file');
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex items-center gap-2">
      {feedback && (
        <span className="text-[11px] text-gray-500" title={feedback}>
          {feedback.length > 60 ? `${feedback.slice(0, 60)}…` : feedback}
        </span>
      )}
      <button
        onClick={download}
        disabled={downloading}
        className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        title="Download a pre-filled xlsx template — fill the Value column, then upload."
      >
        {downloading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
        Download template
      </button>
      <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
        {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
        Upload xlsx
        <input
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={upload}
          disabled={uploading}
        />
      </label>
      {rejected.length > 0 && (
        <button
          onClick={downloadErrors}
          className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100"
          title={`${rejected.length} row(s) could not be uploaded. Download the error list (row, name, code, value, reason), fix them, and re-upload.`}
        >
          <AlertTriangle className="h-3 w-3" />
          Download errors ({rejected.length})
          <Download className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
